import React from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";

import { PlannedDeal } from "store/types";

import { usePortfolioConstructionContext } from "../Context/PortfolioConstructionContext";

interface Props {
  title?: string;
}


const formatDealCategory = (deal: PlannedDeal) => {
  const acName = deal.asset_class?.name ?? "";
  const subName = deal.sub_asset_class?.name;
  return subName ? `${acName}/${subName}` : acName;
};

export const PlannedDealsSidebar: React.FC<Props> = ({ title = "Planned Deals" }) => {
  const { plannedDeals: deals, onAddDeal, onRemoveDeal, onEditDeal } = usePortfolioConstructionContext();

  return (
    <Stack
      sx={{
        width: "100%",
        flex: "1 1 0",
        minHeight: 0,
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "16px",
        gap: "10px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
        overflow: "hidden"
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap="20px" sx={{ width: "100%" }}>
        <Typography
          sx={{
            flex: "1 1 0",
            minWidth: 0,

            fontSize: "18px",
            fontWeight: 500,
            lineHeight: "24px",
            color: "#071f35",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {title}
        </Typography>
        <Button
          onClick={onAddDeal}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          sx={{
            width: "58px",
            height: "24px",
            gap: "0px",
            color: "#071f35",
            backgroundColor: "white",
            border: `1px solid ${"#7b92a9"}`,
            borderRadius: "32px",
            padding: "4px 8px",
            minWidth: 0,
            minHeight: 0,

            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "16px",
            textTransform: "none",
            flexShrink: 0,
            "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
            "&:hover": { backgroundColor: "#f6f8fc", borderColor: "#7b92a9" }
          }}
        >
          Add
        </Button>
      </Stack>

      <Stack sx={{ gap: "10px", width: "100%", overflowY: "auto" }}>
        {deals.map((deal) => {
          const categoryLabel = formatDealCategory(deal);
          return (
          <Box
            key={deal.id}
            sx={{
              width: "100%",
              flexShrink: 0,
              display: "flex",
              alignItems: "stretch",
              border: `1px solid ${"#dbe8f5"}`,
              borderRadius: "4px",
              backgroundColor: "white",
              overflow: "hidden",
              "&:hover": {
                backgroundColor: "#f6f8fc",
                "& .deal-actions": { visibility: "visible", backgroundColor: "#ecf1f8" }
              }
            }}
          >
            <Stack sx={{ flex: 1, minWidth: 0, padding: "8px 12px", gap: "2px" }}>
              <MaterialTooltip title={deal.name}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "24px",
                    color: "#071f35",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {deal.name}
                </Typography>
              </MaterialTooltip>
              <MaterialTooltip title={`${deal.amount} ${deal.currency}`}>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#071f35",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: deal.currency }).format(deal.amount)}
    
                </Typography>
              </MaterialTooltip>
              <MaterialTooltip title={categoryLabel}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: "#496682",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {categoryLabel}
                </Typography>
              </MaterialTooltip>
            </Stack>
            <Stack
              className="deal-actions"
              sx={{
                visibility: "hidden",
                display: "flex",
                width: "40px",
                backgroundColor: "transparent",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                padding: "0 4px",
                flexShrink: 0
              }}
            >
              <IconButton
                onClick={() => onRemoveDeal?.(deal)}
                sx={{
                  padding: "4px",
                  backgroundColor: "rgba(255, 255, 255, 0.96)",
                  color: "#071f35",
                  "&:hover": { backgroundColor: "white" }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={() => onEditDeal?.(deal)}
                sx={{
                  padding: "4px",
                  backgroundColor: "rgba(255, 255, 255, 0.96)",
                  color: "#071f35",
                  "&:hover": { backgroundColor: "white" }
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};
